/* eslint-disable complexity */
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  FormCheckbox,
  FormRadioGroup,
  FormSelect,
  FormTextInput
} from "protocol-setup/components/FormComponents";

import { useRequestHistorySubmit } from "protocol-setup/helpers/withRequestHistory";
import {
  FormButtonGroup,
  FormButtonGroupHeader
} from "protocol-setup/components/FormComponents/FormButtonGroup";
import FormDatePicker from "protocol-setup/components/FormComponents/FormDatePicker";
import SupplementalChecklist from "protocol-setup/components/SupplementChecklist";
import { yupResolver } from "@hookform/resolvers/yup";
import { putWithAuth } from "protocol-setup/redux/makeFetch";
import { raveSetup } from "protocol-setup/redux/constants";
import Text from "protocol-setup/components/Text";
import {
  OpenSuccessNotification,
  OpenErrorNotification
} from "protocol-setup/components/notification";
import { raveStudyGroupsLOVs } from "protocol-setup/redux/useLovs";
import { mapDefaultValues } from "protocol-setup/helpers";
import raveSetupFormSchema from "./raveSetupSchema";
import { tabNames } from "./orHelpers";

import {
  raveProtocolOptions,
  openRaveVersionOptions,
  patientSelectionMethodOptions,
  raveCtepAersIntOptions,
  tsdvSiteAuditingOptions,
  medidataPatientCloudEproOptions,
  requiredTriadTestingOptions,
  dqpSupportedOptions,
  studyRaveCalendaringOptions,
  sdpSupportedOptions
} from "./Lovs";

const onSubmit = async (data, onSuccessCallback, event) => {
  event.preventDefault()
  let payload = {};
  if (typeof data.isRaveProtocol === "boolean" && data.isRaveProtocol) {
    payload = data;
  } else {
    payload.isRaveProtocol = data.isRaveProtocol;
  }

  payload.protocolNumber = data.protocolNumber;
  payload.requestNumber = data.requestNumber;

  delete payload.cmEffectiveCheckBox;
  delete payload.ctepAersInitialSite;
  try {
    await putWithAuth(raveSetup, payload);
    onSuccessCallback();
    OpenSuccessNotification({
      message: data?.psrId
        ? "Record successfully updated."
        : "Record successfully created.",
      autoHide: true
    });
  } catch (error) {
    OpenErrorNotification({
      message:
        "Unable to complete the request. Contact CTSU Help Desk, ctsucontact@westat.com.",
      autoHide: true
    });
  }
};

export const RaveSetupFormCore = ({
  defaultValues,
  raveInstanceOptions,
  selectedRequest,
  setDirtyTab,
  isFormReadOnly,
  fetchPsData,
  isSubmitFormEnabled
}) => {
  const [isDateDisabled, setIsDateDisabled] = useState(true);
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
    formState: { errors, isDirty, dirtyFields }
  } = useForm({
    resolver: yupResolver(raveSetupFormSchema),
    defaultValues
  });

  useEffect(() => {
    setDirtyTab(isDirty);
  }, [isDirty]);

  const memoizedSelectedRequest = useMemo(
    () => selectedRequest,
    [selectedRequest]
  );

  useEffect(() => {
    if (memoizedSelectedRequest) {
      const newValues = mapDefaultValues(
        memoizedSelectedRequest,
        defaultValues,
        {}
      );
      reset(newValues);
    }
  }, [memoizedSelectedRequest, reset]);

  const onSuccess = async () => {
    await fetchPsData();
  };

  const resetForm = () =>
    reset(defaultValues, {
      keepIsValid: false
    });

  // Wrap onSubmit with request history tracking
  const onSubmitWithHistory = useRequestHistorySubmit(
    (data, onSuccessCallback, event) =>
      onSubmit(data, onSuccessCallback, event),
    selectedRequest?.psrId ? dirtyFields : { "initialSave": true },
    tabNames.RAVE_SETUP,
    selectedRequest.psOpenId ? "psrId" : "Initial Save"
  );

  const handleCheckboxChange = (e) => {
    setValue("cmEffectiveCheckBox", e.target.checked);
    setIsDateDisabled(e.target.checked);
    if (e.target.checked) {
      setValue("cmEffectiveDt", "");
    }
  };

  const formTitle = "Rave Setup";

  return (
    <div
      className={`form-layout-wrapper my-3 ${isFormReadOnly ? "read-only" : ""
        }`}
    >
      <form
        onSubmit={handleSubmit((data, event) => {
          const d = {
            ...data,
            protocolNumber: memoizedSelectedRequest.protocolNumber,
            requestNumber: memoizedSelectedRequest.requestNumber,
            psrId: memoizedSelectedRequest.psrId

          }
          onSubmitWithHistory(d, onSuccess, event)
        }
        )}
      >
        <div className="form-layout-header">
          <div className="row d-flex justify-content-end">
            <SupplementalChecklist />
            {isFormReadOnly ? (
              <></>
            ) : (
              <>
                <span>&#124;</span>
                <FormButtonGroupHeader
                  control={control}
                  reset={resetForm}
                  formTitle={formTitle}
                />
              </>
            )}
          </div>
          <div className="form-layout-title invisible">
            Protocol Setup, OPEN
          </div>
        </div>
        <div
          className={`form-layout-body p-0 ${isFormReadOnly ? "pointer-events-none" : ""
            }`}
        >
          <div className="invisible"></div>
          <ul>
            <li className="row form-sub-group-1 px-3">
              <div className="col-md-4 mr-4">
                <FormRadioGroup
                  name="isRaveProtocol"
                  section="openRave"
                  form="raveSetupForm"
                  required
                  control={control}
                  readOnly={isFormReadOnly}
                  options={raveProtocolOptions}
                  onChangeCallBack={(value) => {
                    if (!value) {
                      setValue("raveInstanceUrl", "");
                      setValue("isUatStudyNameAsProd", true);
                      setValue("uatRaveStudyName", "");
                      setValue("openRaveAlsVersion", "");
                      setValue("isRaveCtepAersInt", null);
                      setValue("ctepAersStepNumber", "");
                      setValue("isTsdvSiteAuditing", null);
                      setValue("isDqpSupported", null);
                      setValue("isStudyRaveCalendaring", null);
                      setValue("isSdpSupported", null);
                      setValue("cmEffectiveCheckBox", false);
                      setValue("cmEffectiveDt", "");
                      setValue("cmStepNumber", "");
                      setValue("cmPatientSelectionMethod", "");
                      setValue("cmFirstXPatients", "");
                      setValue("cmNextYPatients", "");
                      setValue("isMedidataPatientCloudEpro", null);
                      setValue("isRequiredTriadTesting", null);
                    } else {
                      const defaultRaveInstanceUrl =
                        raveInstanceOptions.length === 1
                          ? raveInstanceOptions[0].value
                          : "";
                      setValue("raveInstanceUrl", defaultRaveInstanceUrl);
                    }
                  }}
                />
              </div>
              <div className="col-md-4 mr-4"></div>
              <div className="col-md-4"></div>
            </li>

            {watch("isRaveProtocol") === true && (
              <>
                <li className="row form-sub-group-2 px-3">
                  <div className="col-md-4 mr-4">
                    <FormTextInput
                      name="raveInstanceUrl"
                      section="openRave"
                      form="raveSetupForm"
                      control={control}
                      readOnly
                    />
                   
                  </div>
                  <div className="col-md-4 mr-4">
                    <div className="color-dark-blue mr-3">
                      <span className="form-layout-required">*</span>
                      <Text bundle="openRave.raveSetupForm.isUatStudyNameAsProd.checkboxLabel" />
                    </div>
                    <FormCheckbox
                      name="isUatStudyNameAsProd"
                      section="openRave"
                      form="raveSetupForm"
                      control={control}
                      readOnly={isFormReadOnly}
                      className="my-1"
                      checked={
                        typeof watch("isUatStudyNameAsProd") === "boolean" &&
                        watch("isUatStudyNameAsProd")
                      }
                      handleOnclick={(e) => {
                        if (e.target.checked) {
                          setValue("uatRaveStudyName", "");
                          setValue("isUatStudyNameAsProd", true);
                        } else {
                          setValue("isUatStudyNameAsProd", false);
                        }
                      }}
                      wrapperClassName={isFormReadOnly ? 'read-only-show' : ''}
                      disabled={isFormReadOnly}
                    />
                    <FormTextInput
                      name="uatRaveStudyName"
                      label="UAT Rave Study Name"
                      hideLabel
                      control={control}
                      readOnly={isFormReadOnly}
                      trigger={trigger}
                      disabled={
                        typeof watch("isUatStudyNameAsProd") === "boolean" &&
                        watch("isUatStudyNameAsProd")
                      }
                    />
                  </div>
                  <div className="col-md-4"></div>
                </li>

                <li className="row form-sub-group">
                  <div className="col-md-4 mr-4">
                    <FormRadioGroup
                      name="openRaveAlsVersion"
                      section="openRave"
                      form="raveSetupForm"
                      control={control}
                      readOnly={isFormReadOnly}
                      options={openRaveVersionOptions}
                      required
                    />
                  </div>
                  <div className="col-md-4 mr-4">
                    <FormRadioGroup
                      section="openRave"
                      form="raveSetupForm"
                      name="isRaveCtepAersInt"
                      control={control}
                      readOnly={isFormReadOnly}
                      options={raveCtepAersIntOptions}
                      onChangeCallBack={(value) => {
                        if (value === false) {
                          setValue("ctepAersInitialSite", false);
                        }
                      }}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    {watch("isRaveCtepAersInt") === true && (
                      <FormTextInput
                        name="ctepAersStepNumber"
                        section="openRave"
                        form="raveSetupForm"
                        control={control}
                        readOnly={isFormReadOnly}
                        type="text"
                        trigger={trigger}
                        required
                      />
                    )}
                  </div>
                </li>

                <li className="row form-sub-group-4 px-3">
                  <div className="col-md-4 mr-4">
                    <FormRadioGroup
                      name="isTsdvSiteAuditing"
                      section="openRave"
                      form="raveSetupForm"
                      control={control}
                      readOnly={isFormReadOnly}
                      options={tsdvSiteAuditingOptions}
                      required
                    />
                  </div>
                  <div className="col-md-4 mr-4">
                    <FormRadioGroup
                      name="isMedidataPatientCloudEpro"
                      section="openRave"
                      form="raveSetupForm"
                      control={control}
                      readOnly={isFormReadOnly}
                      required
                      options={medidataPatientCloudEproOptions}
                    />
                  </div>
                  <div className="col-md-4">
                    <FormRadioGroup
                      name="isRequiredTriadTesting"
                      section="openRave"
                      form="raveSetupForm"
                      required
                      control={control}
                      readOnly={isFormReadOnly}
                      options={requiredTriadTestingOptions}
                    />
                  </div>
                </li>

                <li className="row form-sub-group">
                  <div className="col-md-4 mr-4">
                    <FormRadioGroup
                      name="isDqpSupported"
                      section="openRave"
                      form="raveSetupForm"
                      control={control}
                      readOnly={isFormReadOnly}
                      options={dqpSupportedOptions}
                      required
                      onChangeCallBack={(value) => {
                        if (value === false) {
                          setValue("isStudyRaveCalendaring", null);
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-4 mr-4">
                    {typeof watch("isDqpSupported") === "boolean" &&
                      watch("isDqpSupported") && (
                        <>
                          <FormRadioGroup
                            name="isStudyRaveCalendaring"
                            section="openRave"
                            form="raveSetupForm"
                            control={control}
                            readOnly={isFormReadOnly}
                            options={studyRaveCalendaringOptions}
                            required
                            helperText={
                              <Text bundle="openRave.raveSetupForm.isStudyRaveCalendaring.helperText" />
                            }
                          />
                        </>
                      )}
                  </div>
                  <div className="col-md-4"></div>
                </li>

                <li className="form-sub-group form-sub-group-5 d-block">
                  <div className="row" style={{ marginBottom: ".75rem" }}>
                    <div className="col-md-4 mr-4">
                      <FormRadioGroup
                        name="isSdpSupported"
                        section="openRave"
                        form="raveSetupForm"
                        required
                        control={control}
                        readOnly={isFormReadOnly}
                        options={sdpSupportedOptions}
                        onChangeCallBack={(value) => {
                          setValue("cmEffectiveCheckBox", value);
                          setValue("cmEffectiveDt", null);
                          setValue("cmStepNumber", null);
                          setValue("cmPatientSelectionMethod", null);
                          setValue("cmFirstXPatients", null);
                          setValue("cmNextYPatients", null);
                          setIsDateDisabled(value);
                          if (!value) {
                            trigger([
                              "cmEffectiveCheckBox",
                              "cmEffectiveDt",
                              "cmStepNumber",
                              "cmPatientSelectionMethod",
                              "cmFirstXPatients",
                              "cmNextYPatients"
                            ]);
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-4 mr-4">
                      {typeof watch("isSdpSupported") === "boolean" &&
                        watch("isSdpSupported") && (
                          <>
                            <div className="extend-label">
                              <span className="form-layout-required">*</span>{" "}
                              Effective Date
                            </div>
                            <FormCheckbox
                              name="cmEffectiveCheckBox"
                              section="openRave"
                              form="raveSetupForm"
                              control={control}
                              readOnly={isFormReadOnly}
                              className="my-1"
                              handleOnclick={handleCheckboxChange}
                              wrapperClassName={isFormReadOnly ? 'read-only-show' : ''}
                              disabled={isFormReadOnly}
                            />
                            <FormDatePicker
                              name="cmEffectiveDt"
                              section="openRave"
                              form="raveSetupForm"
                              hideLabel
                              disabled={isDateDisabled}
                              control={control}
                              readOnly={isFormReadOnly}
                              format="dd-MMM-yyyy"
                            />
                          </>
                        )}
                    </div>
                    <div className="col-md-4">
                      {typeof watch("isSdpSupported") === "boolean" &&
                        watch("isSdpSupported") && (
                          <FormTextInput
                            name="cmStepNumber"
                            section="openRave"
                            form="raveSetupForm"
                            type="text"
                            control={control}
                            readOnly={isFormReadOnly}
                            required
                            trigger={trigger}
                          />
                        )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-4 mr-4">
                      {typeof watch("isSdpSupported") === "boolean" &&
                        watch("isSdpSupported") && (
                          <FormSelect
                            name="cmPatientSelectionMethod"
                            section="openRave"
                            form="raveSetupForm"
                            required
                            control={control}
                            readOnly={isFormReadOnly}
                            options={patientSelectionMethodOptions}
                            onChangeCallback={() => {
                              setValue("cmFirstXPatients", null);
                              setValue("cmNextYPatients", null);
                              trigger("cmFirstXPatients");
                              trigger("cmNextYPatients");
                            }}
                          />
                        )}
                    </div>
                    <div className="col-md-4 mr-4">
                      {(watch("cmPatientSelectionMethod") ===
                        "First X by Arm, then every Y by Arm" ||
                        watch("cmPatientSelectionMethod") ===
                        "First X then every Y patient") && (
                          <FormTextInput
                            name="cmFirstXPatients"
                            section="openRave"
                            form="raveSetupForm"
                            control={control}
                            readOnly={isFormReadOnly}
                            type="text"
                            required
                            trigger={trigger}
                          />
                        )}
                    </div>
                    <div className="col-md-4">
                      {(watch("cmPatientSelectionMethod") ===
                        "First X by Arm, then every Y by Arm" ||
                        watch("cmPatientSelectionMethod") ===
                        "First X then every Y patient") && (
                          <FormTextInput
                            name="cmNextYPatients"
                            section="openRave"
                            form="raveSetupForm"
                            control={control}
                            readOnly={isFormReadOnly}
                            type="text"
                            required
                            trigger={trigger}
                          />
                        )}
                    </div>
                  </div>
                </li>
              </>
            )}
          </ul>
        </div>
        <div className="form-layout-footer">
          {isFormReadOnly ? (
            <></>
          ) : (
            <FormButtonGroup
              control={control}
              readOnly={isFormReadOnly}
              errors={errors}
              reset={resetForm}
              formTitle={formTitle}
              requestNumber={selectedRequest?.requestNumber}
              formPartType={selectedRequest?.formPartType}
              handleSubmit={handleSubmit}
              isSubmitFormEnabled={isSubmitFormEnabled}
              onSuccess={onSuccess}
            />
          )}
        </div>
      </form>
    </div>
  );
};

const RaveSetupForm = ({
  selectedRequest,
  requestNumber,
  setDirtyTab,
  isFormReadOnly,
  fetchPsData,
  isSubmitFormEnabled
}) => {
  const { raveStudyGroups = [] } = raveStudyGroupsLOVs(selectedRequest?.lpo);
  const fieldConfig = {
    requestNumber: null,
    protocolNumber: "",
    isRaveProtocol: null,
    raveInstanceUrl: "",
    isUatStudyNameAsProd: true,
    uatRaveStudyName: "",
    openRaveAlsVersion: "",
    isRaveCtepAersInt: null,
    ctepAersStepNumber: "",
    isTsdvSiteAuditing: null,
    isDqpSupported: null,
    isStudyRaveCalendaring: null,
    isSdpSupported: null,
    cmEffectiveCheckBox: true,
    cmEffectiveDt: "",
    cmStepNumber: "",
    cmPatientSelectionMethod: "",
    cmFirstXPatients: "",
    cmNextYPatients: "",
    isMedidataPatientCloudEpro: null,
    isRequiredTriadTesting: null,
    psrId: null
  };

  const defaultValues = mapDefaultValues(selectedRequest, fieldConfig, {
    requestNumber: selectedRequest?.requestNumber || requestNumber || null,
    protocolNumber:
      selectedRequest?.protocol || selectedRequest?.protocolNumber || "",
    raveInstanceUrl:
      selectedRequest?.raveInstanceUrl ??
      (raveStudyGroups.length === 1 ? raveStudyGroups[0].value : ""),
    isUatStudyNameAsProd: selectedRequest?.isUatStudyNameAsProd ?? true,
    cmEffectiveCheckBox: !selectedRequest?.cmEffectiveDt,
    psrId: selectedRequest?.psrId ?? null
  });

  return (
    <div className="rave-setup-form">
      <RaveSetupFormCore
        defaultValues={defaultValues}
        raveInstanceOptions={raveStudyGroups}
        selectedRequest={selectedRequest}
        setDirtyTab={setDirtyTab}
        isFormReadOnly={isFormReadOnly}
        fetchPsData={fetchPsData}
        isSubmitFormEnabled={isSubmitFormEnabled}
      />
    </div>
  );
};
export default RaveSetupForm;
